import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Plus, CheckSquare, Gift, Users } from 'lucide-react';
import { get, post } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { ApprovalQueue, PointBalanceBanner } from '@/widgets/index';
import { Navbar } from '@/widgets/Navbar';
import { Card, Button, Modal } from '@/shared/ui';
import { formatRelativeTime } from '@/shared/utils/format';
import type { LedgerTransaction } from '@/entities/ledger';
import type { FamilyMember } from '@/entities/user';
import type { Chore } from '@/entities/chore';
import type { Reward } from '@/entities/reward';

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------
interface FamilyMembersResponse {
  members: FamilyMember[];
}

interface ChoresResponse {
  chores: Chore[];
}

interface RewardsResponse {
  rewards: Reward[];
}

interface LedgerResponse {
  entries: LedgerTransaction[];
}

// ---------------------------------------------------------------------------
// Award Points modal
// ---------------------------------------------------------------------------
interface AwardPointsModalProps {
  child: FamilyMember;
  familyId: string;
  chores: Chore[];
  onClose: () => void;
}

function AwardPointsModal({ child, familyId, chores, onClose }: AwardPointsModalProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [selectedChoreId, setSelectedChoreId] = useState('');
  const [points, setPoints] = useState(10);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const relevantChores = chores.filter(
    (c) => c.isActive && !c.isArchived && (c.assignedTo === 'any' || c.assignedTo === child.memberId),
  );

  const handleChoreChange = (choreId: string) => {
    setSelectedChoreId(choreId);
    if (choreId) {
      const chore = chores.find((c) => c.id === choreId);
      if (chore) {
        setPoints(chore.pointValue);
        setNote(chore.name);
      }
    }
  };

  const awardMutation = useMutation({
    mutationFn: () =>
      post<void>(`/families/${familyId}/ledger/adjustment`, {
        childId: childBalanceId,
        points,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['balance', familyId, childBalanceId] });
      void queryClient.invalidateQueries({ queryKey: ['ledger', familyId] });
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to award points');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!points || points < 1 || !Number.isInteger(points)) {
      setError('Points must be a positive whole number');
      return;
    }
    awardMutation.mutate();
  };

  // Use uid when available, fall back to memberId for children without a login account yet.
  const childBalanceId = child.uid ?? child.memberId;

  return (
    <Modal isOpen onClose={onClose} title={`Award Points — ${child.displayName}`} size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Chore picker */}
        {relevantChores.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="chore-select">
              Chore (optional)
            </label>
            <select
              id="chore-select"
              value={selectedChoreId}
              onChange={(e) => handleChoreChange(e.target.value)}
              className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">No chore — custom award</option>
              {relevantChores.map((chore) => (
                <option key={chore.id} value={chore.id}>
                  {chore.name} ({chore.pointValue} pts)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Points */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="points-input">
            Points to award
          </label>
          <input
            id="points-input"
            type="number"
            min={1}
            max={10000}
            value={points}
            onChange={(e) => setPoints(parseInt(e.target.value, 10) || 0)}
            className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Note */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="note-input">
            Note (optional)
          </label>
          <input
            id="note-input"
            type="text"
            placeholder="e.g. Great job today!"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {error && <p className="text-xs text-danger-600" role="alert">{error}</p>}

        <div className="flex gap-2 pt-1">
          <Button type="submit" isLoading={awardMutation.isPending} fullWidth>
            Award {points > 0 ? points.toLocaleString() : 0} pts
          </Button>
          <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Quick stat card
// ---------------------------------------------------------------------------
function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}): React.ReactElement {
  return (
    <Card padding="sm" className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
        {icon}
      </div>
      <div>
        <p className="text-lg font-bold text-slate-900 tabular-nums">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Skeleton stat card
// ---------------------------------------------------------------------------
function SkeletonStatCard(): React.ReactElement {
  return (
    <Card padding="sm" className="flex items-center gap-3 animate-pulse">
      <div className="h-10 w-10 shrink-0 rounded-xl bg-slate-100" />
      <div className="space-y-1.5">
        <div className="h-5 w-10 rounded bg-slate-100" />
        <div className="h-3 w-20 rounded bg-slate-100" />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recent activity item
// ---------------------------------------------------------------------------
function ActivityItem({ tx }: { tx: LedgerTransaction }): React.ReactElement {
  const isPositive = tx.points > 0;
  return (
    <div className="flex items-center justify-between gap-2 py-2.5 border-b border-slate-50 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800">{tx.referenceName}</p>
        <p className="text-xs text-slate-400">
          {tx.childName} · {formatRelativeTime(tx.createdAt)}
        </p>
      </div>
      <span
        className={`shrink-0 text-sm font-semibold tabular-nums ${isPositive ? 'text-secondary-600' : 'text-danger-600'}`}
      >
        {isPositive ? '+' : ''}{tx.points} pts
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ParentDashboardPage
// ---------------------------------------------------------------------------
export default function ParentDashboardPage(): React.ReactElement {
  const { appUser } = useAuth();
  const familyId = appUser?.familyId ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [awardTarget, setAwardTarget] = useState<FamilyMember | null>(null);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const todayStr = format(new Date(), 'EEEE, MMMM d');

  // Data queries
  const { data: membersData, isLoading: membersLoading } = useQuery<FamilyMembersResponse>({
    queryKey: ['members', familyId],
    queryFn: () => get<FamilyMembersResponse>(`/families/${familyId}/members`),
    enabled: Boolean(familyId),
  });

  const { data: choresData, isLoading: choresLoading } = useQuery<ChoresResponse>({
    queryKey: ['chores', familyId],
    queryFn: () => get<ChoresResponse>(`/families/${familyId}/chores?active=true`),
    enabled: Boolean(familyId),
  });

  const { data: rewardsData } = useQuery<RewardsResponse>({
    queryKey: ['rewards', familyId],
    queryFn: () => get<RewardsResponse>(`/families/${familyId}/rewards?active=true`),
    enabled: Boolean(familyId),
  });

  const { data: txData, isLoading: txLoading } = useQuery<LedgerResponse>({
    queryKey: ['ledger', familyId, 'recent'],
    queryFn: () => get<LedgerResponse>(`/families/${familyId}/ledger?limit=10`),
    enabled: Boolean(familyId),
  });

  // Mutations for ApprovalQueue
  const approveChoreMutation = useMutation({
    mutationFn: (completionId: string) =>
      post<void>(`/families/${familyId}/completions/${completionId}/approve`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['completions', familyId] });
      void queryClient.invalidateQueries({ queryKey: ['ledger', familyId] });
      void queryClient.invalidateQueries({ queryKey: ['balance', familyId] });
    },
  });

  const rejectChoreMutation = useMutation({
    mutationFn: ({ completionId, note }: { completionId: string; note: string }) =>
      post<void>(`/families/${familyId}/completions/${completionId}/reject`, { note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['completions', familyId] });
    },
  });

  const approveRedemptionMutation = useMutation({
    mutationFn: (redemptionId: string) =>
      post<void>(`/families/${familyId}/redemptions/${redemptionId}/approve`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['redemptions', familyId] });
      void queryClient.invalidateQueries({ queryKey: ['balance', familyId] });
    },
  });

  const rejectRedemptionMutation = useMutation({
    mutationFn: ({ redemptionId, note }: { redemptionId: string; note: string }) =>
      post<void>(`/families/${familyId}/redemptions/${redemptionId}/reject`, { note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['redemptions', familyId] });
    },
  });

  // Unified approve/reject handlers for ApprovalQueue
  const handleApprove = (id: string) => {
    // ApprovalQueue passes completionId for both chores and rewards — we try both
    approveChoreMutation.mutate(id);
    approveRedemptionMutation.mutate(id);
  };

  const handleReject = (id: string, note: string) => {
    rejectChoreMutation.mutate({ completionId: id, note });
    rejectRedemptionMutation.mutate({ redemptionId: id, note });
  };

  const children = (membersData?.members ?? []).filter((m) => m.role === 'child');
  const activeChores = choresData?.chores ?? [];
  const activeRewards = rewardsData?.rewards ?? [];
  const transactions = txData?.entries ?? [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-slate-900">
            {greeting()}, {appUser?.displayName ?? 'there'}!
          </h1>
          <p className="text-sm text-slate-500">{todayStr}</p>
        </div>

        {/* Approval Queue — #1 priority, full width */}
        <ApprovalQueue
          familyId={familyId}
          onApprove={handleApprove}
          onReject={handleReject}
        />

        {/* Quick stats */}
        <section aria-label="Quick stats">
          <h2 className="mb-3 text-sm font-semibold text-slate-500 uppercase tracking-wide">
            Overview
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {choresLoading ? (
              <>
                <SkeletonStatCard />
                <SkeletonStatCard />
              </>
            ) : (
              <>
                <StatCard
                  label="Active chores"
                  value={activeChores.length}
                  icon={<CheckSquare size={20} aria-hidden="true" />}
                />
                <StatCard
                  label="Active rewards"
                  value={activeRewards.length}
                  icon={<Gift size={20} aria-hidden="true" />}
                />
              </>
            )}

            {/* Child balance chips */}
            {membersLoading ? (
              <>
                <SkeletonStatCard />
                <SkeletonStatCard />
              </>
            ) : (
              children.slice(0, 2).map((child) => (
                <div key={child.memberId} className="col-span-1 flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <PointBalanceBanner
                      childId={child.uid ?? child.memberId}
                      familyId={familyId}
                      childName={child.displayName}
                      compact
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setAwardTarget(child)}
                    className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 transition-colors"
                    aria-label={`Award points to ${child.displayName}`}
                  >
                    <Plus size={14} aria-hidden="true" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* More children */}
          {!membersLoading && children.length > 2 && (
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {children.slice(2).map((child) => (
                <div key={child.memberId} className="flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <PointBalanceBanner
                      childId={child.uid ?? child.memberId}
                      familyId={familyId}
                      childName={child.displayName}
                      compact
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setAwardTarget(child)}
                    className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 transition-colors"
                    aria-label={`Award points to ${child.displayName}`}
                  >
                    <Plus size={14} aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recent activity */}
        <section aria-label="Recent activity">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
              Recent Activity
            </h2>
            <button
              type="button"
              onClick={() => navigate('/ledger')}
              className="text-xs text-primary-600 hover:text-primary-800 font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded"
            >
              View all →
            </button>
          </div>
          {txLoading ? (
            <Card className="animate-pulse space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex justify-between py-2.5 border-b border-slate-50">
                  <div className="space-y-1.5">
                    <div className="h-3.5 w-40 rounded bg-slate-100" />
                    <div className="h-2.5 w-24 rounded bg-slate-100" />
                  </div>
                  <div className="h-3.5 w-14 rounded bg-slate-100" />
                </div>
              ))}
            </Card>
          ) : transactions.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 py-8 text-center">
              <Users size={28} className="text-slate-300" aria-hidden="true" />
              <p className="text-sm text-slate-500">No activity yet. Assign some chores to get started!</p>
            </Card>
          ) : (
            <Card padding="sm">
              {transactions.map((tx) => (
                <ActivityItem key={tx.id} tx={tx} />
              ))}
            </Card>
          )}
        </section>
      </main>

      {/* Floating action button (mobile) */}
      <div className="fixed bottom-6 right-4 flex flex-col gap-3 sm:hidden" role="group" aria-label="Quick actions">
        <button
          type="button"
          onClick={() => navigate('/rewards')}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-white shadow-lg hover:bg-accent-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 transition-colors duration-150"
          aria-label="Add a reward"
        >
          <Gift size={22} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => navigate('/chores')}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors duration-150"
          aria-label="Add a chore"
        >
          <Plus size={26} aria-hidden="true" />
        </button>
      </div>

      {/* Desktop quick actions (inside main, after activity) */}
      <div className="hidden sm:flex mx-auto max-w-7xl px-6 pb-8 gap-3">
        <Button
          variant="primary"
          onClick={() => navigate('/chores')}
          leftIcon={<Plus size={16} />}
        >
          Add a chore
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate('/rewards')}
          leftIcon={<Gift size={16} />}
        >
          Add a reward
        </Button>
      </div>

      {/* Award Points modal */}
      {awardTarget && (
        <AwardPointsModal
          child={awardTarget}
          familyId={familyId}
          chores={activeChores}
          onClose={() => setAwardTarget(null)}
        />
      )}
    </div>
  );
}
